import { ErrorBoundary, TabItem, Tabs } from "@/components";
import { useAuthStore } from "@/store/authStore";
import { useNotificationStore } from "@/store/notificationStore";
import { RoleEnum } from "@/types";
import React, { useMemo } from "react";
import {
  MdOutlineNotifications,
  MdOutlineNotificationsActive,
} from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import MyNotificationTable from "./MyNotification/MyNotificationTable";
import NewNotification from "./NewNotification/NewNotification";
import NotificationTable from "./NotificationTable/NotificationTable";
import { UpdateNotification } from "./UpdateNotification/UpdateNotification";

export enum ENotificationTab {
  NOTIFICATION_MANAGEMENT = 0,
  MY_NOTIFICATION = 1,
}

export const KEYS_NOTIFICATION_SEARCH_PARAMS = {
  TAB: "tab",
  SEARCH: "s",
  START_DATE: "startDate",
  END_DATE: "endDate",
};

const Notifications: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const tab =
    +params[KEYS_NOTIFICATION_SEARCH_PARAMS.TAB] ||
    ENotificationTab.NOTIFICATION_MANAGEMENT;

  const {
    isEditModalOpen,
    closeEditModal,
    isCreateModalOpen,
    closeCreateModal,
    openCreateModal,
  } = useNotificationStore();

  const closeCreateDrawer = () => {
    closeCreateModal();
  };

  // Với role user hoặc other ẩn button tạo mới, ẩn cột status
  const { user } = useAuthStore();
  const role = user?.role.name;

  // Với role là user hoặc other ẩn button tạo mới, show bảng my notification
  const isMember = role === RoleEnum.USER || role === RoleEnum.OTHER;

  const TabList: TabItem[] = useMemo(
    () =>
      [
        !!role && {
          label: (
            <p className="flex items-center gap-2">
              <MdOutlineNotificationsActive className="size-5" />
              通知を送信しました
            </p>
          ),
          key: ENotificationTab.NOTIFICATION_MANAGEMENT,
          children: (
            <NotificationTable role={role} setOpenNew={openCreateModal} />
          ),
        },
        {
          label: (
            <p className="flex items-center gap-2">
              <MdOutlineNotifications className="size-5" />
              通知を受信しました
            </p>
          ),
          key: ENotificationTab.MY_NOTIFICATION,
          children: <MyNotificationTable />,
        },
      ].filter(Boolean) as TabItem[],
    [role]
  );

  const handleTabChange = (newTab: number) => {
    const updatedParams = {
      [KEYS_NOTIFICATION_SEARCH_PARAMS.TAB]: newTab.toString(),
    };
    setSearchParams(updatedParams, { replace: true });
  };

  return (
    <ErrorBoundary>
      <div className="pb-10">
        <section className="flex flex-col h-[5rem] gap-1 justify-center">
          <h1 className="text-lg font-bold leading-7 whitespace-nowrap">
            通知管理
          </h1>
          <p className="text-light_secondary font-normal text-xs leading-4.5">
          タイヤ公取協からのお知らせや、各機能に関するお知らせを時系列で掲載しています。クリックして内容をご確認ください。
          </p>
        </section>

        {!!role ? (
          <>
            {isMember ? (
              <MyNotificationTable isMember={isMember} /> // => role member show table my notification
            ) : (
              <Tabs
                tabs={TabList}
                keyActive={tab}
                onTabChange={handleTabChange}
              />
            )}
          </>
        ) : null}
      </div>

      {isCreateModalOpen && (
        <NewNotification open={isCreateModalOpen} onClose={closeCreateDrawer} />
      )}

      {isEditModalOpen && (
        <UpdateNotification
          label="更新通知"
          open={isEditModalOpen}
          onClose={closeEditModal}
          size="medium"
          disableBackdropClick
        />
      )}
    </ErrorBoundary>
  );
};

export default Notifications;
